import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from '../IconButton/index.js';
import { Button } from '../Button/index.js';
import { useI18nT } from '../../i18n/index.js';
import { ensurePortalRoot } from '../shared/index.js';
import { defaultIconForTone, IcnClose } from './icons.js';
import type { ToastApi, ToastOptions, ToastPlacement, ToastRecord, ToastTone } from './types.js';

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_DURATION: Record<ToastTone, number | null> = {
  neutral: 4000,
  info: 5000,
  success: 3500,
  warning: 6000,
  danger: null, // sticky — user needs to see failure
  accent: 4000,
};

const EXIT_MS = 180;

export interface ToastProviderProps {
  children: ReactNode;
  placement?: ToastPlacement;
  /** Maximum simultaneous toasts. Oldest are dismissed when exceeded. */
  max?: number;
}

/** Wrap your app once. Inside, components call `useToast()` to push toasts.
 *  Stacks live in a portal so z-index / overflow can't clip them. */
export function ToastProvider(props: ToastProviderProps): ReactNode {
  const t = useI18nT();
  const { children, placement = 'bottom-right', max = 5 } = props;
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const timers = useRef(new Map<string, number>());

  // Per-toast play-state — separate from the public ToastRecord because
  // it's pure timer bookkeeping, not part of the rendered shape.
  //
  //   resumedAt:  wall-clock time the current play period started (NaN
  //               when paused).
  //   consumedMs: cumulative time the toast has been "playing" before
  //               the current play period; pause adds the elapsed since
  //               resumedAt to this and freezes resumedAt.
  //   duration:   cached so resume math doesn't need the rec object.
  //
  // remaining = max(0, duration - consumedMs - (Date.now() - resumedAt))
  // when playing, or = max(0, duration - consumedMs) when paused.
  const playState = useRef(
    new Map<string, { resumedAt: number; consumedMs: number; duration: number }>(),
  );

  const clearTimer = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t !== undefined) {
      window.clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  const remove = useCallback(
    (id: string) => {
      clearTimer(id);
      playState.current.delete(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    },
    [clearTimer],
  );

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
      window.setTimeout(() => {
        remove(id);
      }, EXIT_MS);
    },
    [clearTimer, remove],
  );

  /** Start (or restart) the dismiss timer for `id`. The toast is given
   *  `remaining` ms to live; play-state is updated so subsequent
   *  pause/resume cycles compute the right answer. Pass `consumedMs=0`
   *  for a fresh schedule (e.g. on `show()`); pass the existing value
   *  to resume mid-play. */
  const startTimer = useCallback(
    (id: string, duration: number, consumedMs: number) => {
      clearTimer(id);
      const remaining = Math.max(0, duration - consumedMs);
      playState.current.set(id, { resumedAt: Date.now(), consumedMs, duration });
      const handle = window.setTimeout(() => {
        dismiss(id);
      }, remaining);
      timers.current.set(id, handle);
    },
    [clearTimer, dismiss],
  );

  const show = useCallback(
    (opts: ToastOptions): string => {
      const id = opts.id ?? `toast-${String(Date.now())}-${String(Math.random()).slice(2, 7)}`;
      const tone: ToastTone = opts.tone ?? 'neutral';
      const duration = opts.duration ?? DEFAULT_DURATION[tone];
      setToasts((prev) => {
        const existing = prev.find((t) => t.id === id);
        const next: ToastRecord = {
          ...opts,
          id,
          tone,
          duration,
          createdAt: existing?.createdAt ?? Date.now(),
          leaving: false,
        };
        const without = prev.filter((t) => t.id !== id);
        const capped = [...without, next].slice(-max);
        // dismiss any that got dropped from capping
        if (capped.length < without.length + 1) {
          for (const dropped of without.slice(0, without.length + 1 - capped.length)) {
            clearTimer(dropped.id);
            playState.current.delete(dropped.id);
          }
        }
        return capped;
      });
      // `show()` resets the play clock — calling show on an existing id
      // (toast update) gives the user a fresh full-duration window.
      if (duration === null || duration <= 0) {
        clearTimer(id);
        playState.current.delete(id);
      } else {
        startTimer(id, duration, 0);
      }
      return id;
    },
    [max, startTimer, clearTimer],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (o) => show({ tone: 'success', ...o }),
      info: (o) => show({ tone: 'info', ...o }),
      warning: (o) => show({ tone: 'warning', ...o }),
      danger: (o) => show({ tone: 'danger', ...o }),
      dismiss,
      dismissAll: () => {
        for (const t of toasts) dismiss(t.id);
      },
    }),
    [show, dismiss, toasts],
  );

  // Cleanup timers on unmount.
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const handle of map.values()) window.clearTimeout(handle);
      map.clear();
    };
  }, []);

  const portalRoot = ensurePortalRoot();

  const [pausedIds, setPausedIds] = useState<ReadonlySet<string>>(new Set());

  const pauseAuto = useCallback(
    (id: string) => {
      clearTimer(id);
      // Freeze the play clock: add the time elapsed since the last resume
      // to consumedMs. Keep the entry around (with the same duration) so
      // resume can compute remaining = duration - consumedMs.
      const state = playState.current.get(id);
      if (state !== undefined) {
        const elapsedThisPeriod = Date.now() - state.resumedAt;
        playState.current.set(id, {
          ...state,
          consumedMs: state.consumedMs + elapsedThisPeriod,
          // resumedAt is meaningless while paused; set to NaN to surface
          // any accidental use as obviously wrong rather than silently
          // double-counting time.
          resumedAt: Number.NaN,
        });
      }
      setPausedIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    },
    [clearTimer],
  );

  const resumeAuto = useCallback(
    (rec: ToastRecord) => {
      setPausedIds((prev) => {
        if (!prev.has(rec.id)) return prev;
        const next = new Set(prev);
        next.delete(rec.id);
        return next;
      });
      if (rec.duration === null || rec.duration === undefined || rec.duration <= 0) return;
      // Resume from the consumedMs we accumulated during pause cycles. A
      // brief floor (300ms) gives the user a chance to re-read the toast
      // even if they hovered until almost the end.
      const state = playState.current.get(rec.id);
      const consumedMs = state?.consumedMs ?? 0;
      const remainingNatural = Math.max(0, rec.duration - consumedMs);
      const remaining = Math.max(300, remainingNatural);
      // If the floor extended us, roll the clock back so the play state
      // stays internally consistent (consumedMs + remaining === duration
      // when remaining > 0, modulo the floor).
      const adjustedConsumed = rec.duration - remaining;
      startTimer(rec.id, rec.duration, Math.max(0, adjustedConsumed));
    },
    [startTimer],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {portalRoot !== null
        ? createPortal(
            <div
              className="vq-toast-region"
              data-component="ToastRegion"
              data-placement={placement}
              role="region"
              aria-label={t('common.loading') /* semantic fallback */}
              aria-live="polite"
              aria-atomic="false"
            >
              {toasts.map((rec) => {
                const tone = rec.tone ?? 'neutral';
                const sticky =
                  rec.duration === null || rec.duration === undefined || rec.duration <= 0;
                const paused = pausedIds.has(rec.id);
                const showIcon = rec.icon !== null;
                const iconNode =
                  rec.icon !== undefined && rec.icon !== null ? rec.icon : defaultIconForTone(tone);
                return (
                  <div
                    key={rec.id}
                    className="vq-toast"
                    data-component="Toast"
                    data-tone={tone}
                    data-leaving={rec.leaving ? 'true' : undefined}
                    data-sticky={sticky ? 'true' : undefined}
                    data-paused={paused ? 'true' : undefined}
                    role={tone === 'danger' ? 'alert' : 'status'}
                    style={
                      rec.duration !== null && rec.duration !== undefined && rec.duration > 0
                        ? ({ '--vq-toast-duration': `${String(rec.duration)}ms` } as Record<
                            string,
                            string
                          >)
                        : undefined
                    }
                    onMouseEnter={() => {
                      pauseAuto(rec.id);
                    }}
                    onMouseLeave={() => {
                      resumeAuto(rec);
                    }}
                    onFocus={() => {
                      pauseAuto(rec.id);
                    }}
                    onBlur={() => {
                      resumeAuto(rec);
                    }}
                  >
                    {showIcon ? (
                      <span className="vq-toast__icon" aria-hidden="true">
                        {iconNode}
                      </span>
                    ) : null}
                    <div className="vq-toast__body">
                      {rec.title !== undefined ? (
                        <div className="vq-toast__title">{rec.title}</div>
                      ) : null}
                      {rec.description !== undefined ? (
                        <div className="vq-toast__desc">{rec.description}</div>
                      ) : null}
                    </div>
                    {rec.action !== undefined ? (
                      <div className="vq-toast__action">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            rec.action?.onClick();
                            dismiss(rec.id);
                          }}
                        >
                          {rec.action.label}
                        </Button>
                      </div>
                    ) : null}
                    {(rec.dismissible ?? true) ? (
                      <IconButton
                        className="vq-toast__close"
                        variant="ghost"
                        size="sm"
                        label={t('common.close')}
                        onClick={() => {
                          dismiss(rec.id);
                        }}
                      >
                        <IcnClose size={12} />
                      </IconButton>
                    ) : null}
                    {!sticky ? <span className="vq-toast__progress" aria-hidden="true" /> : null}
                  </div>
                );
              })}
            </div>,
            portalRoot,
          )
        : null}
    </ToastContext.Provider>
  );
}

/** Access the toast API. Must be rendered under a <ToastProvider>. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast() must be used inside <ToastProvider>');
  }
  return ctx;
}
